/**
 * drilldownsExtended.ts
 *
 * All additional drill-down loader functions beyond the original 8 in drilldowns.ts.
 * Each function returns a DrilldownResult — columns + rows — that reconciles
 * to the value shown on the corresponding dashboard card.
 *
 * RLS is enforced at the Supabase layer; company_id filtering is an extra guard.
 */

import { supabase } from '../supabase';
import { fetchAllChunked } from './pagination';
import type { DrilldownResult } from './drilldowns';

// ── Shared helpers ────────────────────────────────────────────

function co<T>(builder: T, companyId?: string | null): T {
  return companyId
    ? (builder as unknown as { eq: (c: string, v: string) => T }).eq('company_id', companyId)
    : builder;
}

const monthStart = () =>
  new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();

const ninetyDaysAgo = () =>
  new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);

const thirtyDaysAgo = () =>
  new Date(Date.now() - 30 * 86400000).toISOString();

/** inventory_items / cycle_count_items / mrp_planned_orders carry only product_id; code + name come from products. */
const productOf = (r: Record<string, unknown>) => {
  const p = r.product as { code?: string | null; name?: string | null } | null;
  return { code: String(p?.code ?? '—'), name: String(p?.name ?? '—') };
};

/** approval_requests.submitted_by references auth.users (not profiles) — resolve names via profiles.user_id. */
async function submitterNames(rows: Record<string, unknown>[]): Promise<Map<string, string>> {
  const ids = [...new Set(rows.map(r => String(r.submitted_by ?? '')).filter(Boolean))];
  if (ids.length === 0) return new Map();
  const { data } = await supabase.from('profiles').select('user_id, full_name').in('user_id', ids);
  return new Map((data ?? []).map((p: Record<string, unknown>) => [String(p.user_id), String(p.full_name ?? '')]));
}

const submitterOf = (names: Map<string, string>, r: Record<string, unknown>) =>
  names.get(String(r.submitted_by ?? '')) || '—';

// ── Finance ───────────────────────────────────────────────────

export async function drillRevenueMtd(companyId?: string | null): Promise<DrilldownResult> {
  // Reduced total is the Revenue MTD figure: chunked full fetch — PostgREST
  // db-max-rows silently caps single responses at 1000, even with .limit(2000).
  const data = await fetchAllChunked<Record<string, unknown>>((cursor, chunk) => {
    let q = co(
      supabase
        .from('invoices')
        .select('id, invoice_number, total, tax_amount, status, created_at, customer:customers!customer_id(name)')
        .in('status', ['issued', 'paid', 'partial'])
        .gte('created_at', monthStart())
        .is('deleted_at', null)
        .order('id', { ascending: true }).limit(chunk),
      companyId,
    );
    if (cursor) q = q.gt('id', cursor);
    return q;
  });
  data.sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));
  const rows = data.map((i: Record<string, unknown>) => ({
    invoice_number: String(i.invoice_number ?? '—'),
    customer: (i.customer as { name: string } | null)?.name ?? '—',
    status: String(i.status ?? ''),
    tax_amount: Number(i.tax_amount ?? 0),
    total: Number(i.total ?? 0),
    created_at: String(i.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'customer', label: 'Customer' },
      { key: 'status', label: 'Status' },
      { key: 'tax_amount', label: 'VAT', format: 'currency' },
      { key: 'total', label: 'Total (SAR)', format: 'currency' },
      { key: 'created_at', label: 'Date', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Revenue MTD',
  };
}

export async function drillOverdueReceivables(companyId?: string | null): Promise<DrilldownResult> {
  const today = new Date().toISOString().slice(0, 10);
  // Reduced total is the overdue-AR figure: chunked full fetch (see drillRevenueMtd).
  const data = await fetchAllChunked<Record<string, unknown>>((cursor, chunk) => {
    let q = co(
      supabase
        .from('invoices')
        .select('id, invoice_number, total, paid_amount, due_date, customer:customers!customer_id(name)')
        .in('status', ['issued', 'partial', 'overdue'])
        .lt('due_date', today)
        .is('deleted_at', null)
        .order('id', { ascending: true }).limit(chunk),
      companyId,
    );
    if (cursor) q = q.gt('id', cursor);
    return q;
  });
  data.sort((a, b) => String(a.due_date ?? '').localeCompare(String(b.due_date ?? '')));
  const today_ms = Date.now();
  const rows = data.map((i: Record<string, unknown>) => {
    const due = new Date(String(i.due_date ?? '')).getTime();
    const overdue_days = isNaN(due) ? 0 : Math.floor((today_ms - due) / 86400000);
    return {
      invoice_number: String(i.invoice_number ?? '—'),
      customer: (i.customer as { name: string } | null)?.name ?? '—',
      due_date: String(i.due_date ?? ''),
      overdue_days,
      outstanding: Number(i.total ?? 0) - Number(i.paid_amount ?? 0),
    };
  });
  return {
    columns: [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'customer', label: 'Customer' },
      { key: 'due_date', label: 'Due Date', format: 'date' },
      { key: 'overdue_days', label: 'Days Overdue', format: 'number' },
      { key: 'outstanding', label: 'Outstanding (SAR)', format: 'currency' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.outstanding as number), 0),
    totalLabel: 'Total overdue',
  };
}

export async function drillVatLiability(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('invoices')
      .select('id, invoice_number, total, tax_amount, status, created_at, customer:customers!customer_id(name)')
      .in('status', ['issued', 'partial', 'paid'])
      .not('tax_amount', 'is', null)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((i: Record<string, unknown>) => ({
    invoice_number: String(i.invoice_number ?? '—'),
    customer: (i.customer as { name: string } | null)?.name ?? '—',
    status: String(i.status ?? ''),
    tax_amount: Number(i.tax_amount ?? 0),
    created_at: String(i.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'customer', label: 'Customer' },
      { key: 'status', label: 'Status' },
      { key: 'tax_amount', label: 'VAT Amount (SAR)', format: 'currency' },
      { key: 'created_at', label: 'Date', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.tax_amount as number), 0),
    totalLabel: 'Total VAT liability',
  };
}

export async function drillZatcaRejected(companyId?: string | null): Promise<DrilldownResult> {
  // zatca_invoices holds only hash / uuid / clearance columns. The rejection state, number and
  // amount live on invoices.zatca_status; no rejection-reason column exists on either table.
  // invoice_type (standard = clearance, simplified = reporting) is the ZATCA submission type.
  const { data, error } = await co(
    supabase
      .from('invoices')
      .select('id, invoice_number, invoice_type, total, zatca_status, created_at')
      .eq('zatca_status', 'rejected')
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((z: Record<string, unknown>) => ({
    invoice_number: String(z.invoice_number ?? '—'),
    invoice_type: String(z.invoice_type ?? '—'),
    total: Number(z.total ?? 0),
    zatca_status: String(z.zatca_status ?? ''),
    created_at: String(z.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'invoice_type', label: 'Type' },
      { key: 'total', label: 'Amount (SAR)', format: 'currency' },
      { key: 'zatca_status', label: 'ZATCA Status' },
      { key: 'created_at', label: 'Invoice Date', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total rejected value',
  };
}

export async function drillZatcaGaps(companyId?: string | null): Promise<DrilldownResult> {
  // Invoices issued in last 90 days that never received a ZATCA UUID (never submitted)
  const { data, error } = await co(
    supabase
      .from('invoices')
      .select('id, invoice_number, total, created_at, status')
      .in('status', ['issued', 'paid'])
      .gte('created_at', ninetyDaysAgo())
      .is('zatca_uuid', null)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((i: Record<string, unknown>) => ({
    invoice_number: String(i.invoice_number ?? '—'),
    status: String(i.status ?? ''),
    total: Number(i.total ?? 0),
    created_at: String(i.created_at ?? ''),
    gap_reason: 'Not submitted to ZATCA',
  }));
  return {
    columns: [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'status', label: 'Status' },
      { key: 'total', label: 'Amount (SAR)', format: 'currency' },
      { key: 'created_at', label: 'Date', format: 'date' },
      { key: 'gap_reason', label: 'Gap Reason' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total unsubmitted value',
  };
}

export async function drillBankReconciliationExceptions(
  companyId?: string | null,
): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('bank_transactions')
      .select('id, txn_date, description, amount, is_matched')
      .eq('is_matched', false)
      .order('txn_date', { ascending: false })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((t: Record<string, unknown>) => ({
    txn_date: String(t.txn_date ?? ''),
    description: String(t.description ?? '—'),
    amount: Number(t.amount ?? 0),
  }));
  return {
    columns: [
      { key: 'txn_date', label: 'Date', format: 'date' },
      { key: 'description', label: 'Description' },
      { key: 'amount', label: 'Amount (SAR)', format: 'currency' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.amount as number), 0),
    totalLabel: 'Unreconciled total',
  };
}

export async function drillDepreciationRuns(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('depreciation_run_log')
      .select('id, run_date, total_depreciation, assets_processed, status')
      .order('run_date', { ascending: false })
      .limit(24),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    run_date: String(r.run_date ?? ''),
    assets_processed: Number(r.assets_processed ?? 0),
    total_depreciation: Number(r.total_depreciation ?? 0),
    status: String(r.status ?? ''),
  }));
  return {
    columns: [
      { key: 'run_date', label: 'Run Date', format: 'date' },
      { key: 'assets_processed', label: 'Assets', format: 'number' },
      { key: 'total_depreciation', label: 'Total Depreciation (SAR)', format: 'currency' },
      { key: 'status', label: 'Status' },
    ],
    rows,
  };
}

export async function drillEbitda(companyId?: string | null): Promise<DrilldownResult> {
  // Reduced total is a revenue figure: chunked full fetch (see drillRevenueMtd).
  const data = await fetchAllChunked<Record<string, unknown>>((cursor, chunk) => {
    let q = co(
      supabase
        .from('invoices')
        .select('id, invoice_number, total, status, created_at')
        .in('status', ['paid', 'issued'])
        .gte('created_at', monthStart())
        .is('deleted_at', null)
        .order('id', { ascending: true }).limit(chunk),
      companyId,
    );
    if (cursor) q = q.gt('id', cursor);
    return q;
  });
  data.sort((a, b) => String(b.created_at ?? '').localeCompare(String(a.created_at ?? '')));
  const rows = data.map((i: Record<string, unknown>) => ({
    invoice_number: String(i.invoice_number ?? ''),
    total: Number(i.total ?? 0),
    status: String(i.status ?? ''),
    created_at: String(i.created_at ?? ''),
    category: 'Revenue',
  }));
  return {
    columns: [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'category', label: 'Category' },
      { key: 'status', label: 'Status' },
      { key: 'total', label: 'Amount (SAR)', format: 'currency' },
      { key: 'created_at', label: 'Date', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Revenue component (MTD)',
  };
}

// ── Payroll ───────────────────────────────────────────────────

export async function drillPayrollCost(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('payroll_batches')
      .select('id, period_month, period_year, status, total_gross, total_net, total_gosi_employer, total_employees')
      .in('status', ['approved', 'paid'])
      .order('period_year', { ascending: false })
      .order('period_month', { ascending: false })
      .limit(12),
    companyId,
  );
  if (error) throw new Error(error.message);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const rows = (data ?? []).map((b: Record<string, unknown>) => ({
    period: `${months[(Number(b.period_month) || 1) - 1]} ${b.period_year}`,
    status: String(b.status ?? ''),
    total_employees: Number(b.total_employees ?? 0),
    total_gross: Number(b.total_gross ?? 0),
    total_gosi_employer: Number(b.total_gosi_employer ?? 0),
    total_net: Number(b.total_net ?? 0),
  }));
  return {
    columns: [
      { key: 'period', label: 'Period' },
      { key: 'status', label: 'Status' },
      { key: 'total_employees', label: 'Employees', format: 'number' },
      { key: 'total_gross', label: 'Gross (SAR)', format: 'currency' },
      { key: 'total_gosi_employer', label: 'GOSI Employer (SAR)', format: 'currency' },
      { key: 'total_net', label: 'Net (SAR)', format: 'currency' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total_net as number), 0),
    totalLabel: 'Total payroll net (12 months)',
  };
}

export async function drillPayrollBatches(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('payroll_batches')
      .select('id, period_month, period_year, status, total_gross, total_net, total_employees, created_at')
      .order('period_year', { ascending: false })
      .order('period_month', { ascending: false })
      .limit(24),
    companyId,
  );
  if (error) throw new Error(error.message);
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const rows = (data ?? []).map((b: Record<string, unknown>) => ({
    period: `${months[(Number(b.period_month) || 1) - 1]} ${b.period_year}`,
    status: String(b.status ?? ''),
    total_employees: Number(b.total_employees ?? 0),
    total_gross: Number(b.total_gross ?? 0),
    total_net: Number(b.total_net ?? 0),
    created_at: String(b.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'period', label: 'Period' },
      { key: 'status', label: 'Status' },
      { key: 'total_employees', label: 'Employees', format: 'number' },
      { key: 'total_gross', label: 'Gross (SAR)', format: 'currency' },
      { key: 'total_net', label: 'Net (SAR)', format: 'currency' },
    ],
    rows,
  };
}

// ── HR ────────────────────────────────────────────────────────

export async function drillGosiContributions(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('gosi_submissions')
      .select('id, contribution_month, status, employee_count, total_employee_contribution, total_employer_contribution, submitted_at, mudad_reference')
      .order('contribution_month', { ascending: false })
      .limit(12),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((g: Record<string, unknown>) => ({
    contribution_month: String(g.contribution_month ?? ''),
    status: String(g.status ?? ''),
    employee_count: Number(g.employee_count ?? 0),
    employee_contrib: Number(g.total_employee_contribution ?? 0),
    employer_contrib: Number(g.total_employer_contribution ?? 0),
    total: Number(g.total_employee_contribution ?? 0) + Number(g.total_employer_contribution ?? 0),
    submitted_at: String(g.submitted_at ?? ''),
    mudad_reference: String(g.mudad_reference ?? '—'),
  }));
  return {
    columns: [
      { key: 'contribution_month', label: 'Month' },
      { key: 'status', label: 'Status' },
      { key: 'employee_count', label: 'Employees', format: 'number' },
      { key: 'employee_contrib', label: 'Employee Share (SAR)', format: 'currency' },
      { key: 'employer_contrib', label: 'Employer Share (SAR)', format: 'currency' },
      { key: 'total', label: 'Total (SAR)', format: 'currency' },
      { key: 'submitted_at', label: 'Submitted', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total GOSI (12 months)',
  };
}

export async function drillWpsSubmissions(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('wps_salary_files')
      .select('id, file_name, status, total_employees, total_amount, generated_at, submitted_at')
      .order('generated_at', { ascending: false })
      .limit(24),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((w: Record<string, unknown>) => ({
    file_name: String(w.file_name ?? '—'),
    status: String(w.status ?? ''),
    total_employees: Number(w.total_employees ?? 0),
    total_amount: Number(w.total_amount ?? 0),
    generated_at: String(w.generated_at ?? ''),
    submitted_at: w.submitted_at ? String(w.submitted_at) : '—',
  }));
  return {
    columns: [
      { key: 'file_name', label: 'File' },
      { key: 'status', label: 'Status' },
      { key: 'total_employees', label: 'Employees', format: 'number' },
      { key: 'total_amount', label: 'Amount (SAR)', format: 'currency' },
      { key: 'generated_at', label: 'Generated', format: 'date' },
      { key: 'submitted_at', label: 'Submitted', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total_amount as number), 0),
    totalLabel: 'Total WPS amount',
  };
}

export async function drillLeaveBalance(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('leave_requests')
      .select('id, start_date, end_date, days, status, leave_type:leave_types!leave_type_id(name_en), employee:employees!employee_id(first_name_en, last_name_en)')
      .eq('status', 'pending')
      .order('start_date', { ascending: true })
      // Pending-only requests stay far below the 1000-row server cap
      // (db-max-rows), which is the true maximum a single response can carry.
      .limit(1000),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    employee: (() => {
      const e = r.employee as { first_name_en?: string; last_name_en?: string } | null;
      return e ? `${e.first_name_en ?? ''} ${e.last_name_en ?? ''}`.trim() : '—';
    })(),
    leave_type: (r.leave_type as { name_en: string } | null)?.name_en ?? '—',
    start_date: String(r.start_date ?? ''),
    end_date: String(r.end_date ?? ''),
    days: Number(r.days ?? 0),
  }));
  return {
    columns: [
      { key: 'employee', label: 'Employee' },
      { key: 'leave_type', label: 'Leave Type' },
      { key: 'start_date', label: 'From', format: 'date' },
      { key: 'end_date', label: 'To', format: 'date' },
      { key: 'days', label: 'Days', format: 'number' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.days as number), 0),
    totalLabel: 'Total pending leave days',
  };
}

export async function drillDocumentExpiry(companyId?: string | null): Promise<DrilldownResult> {
  const cutoff = new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10);
  // employee_documents has no company_id — scope via the employee (inner join), not co().
  let q = supabase
    .from('employee_documents')
    .select('id, document_type, expiry_date, employee:employees!employee_id!inner(first_name_en, last_name_en, company_id)')
    .not('expiry_date', 'is', null)
    .lte('expiry_date', cutoff)
    .order('expiry_date', { ascending: true });
  if (companyId) q = q.eq('employees.company_id', companyId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const today_ms = Date.now();
  const rows = (data ?? []).map((d: Record<string, unknown>) => {
    const exp = new Date(String(d.expiry_date ?? '')).getTime();
    const days_remaining = isNaN(exp) ? 0 : Math.round((exp - today_ms) / 86400000);
    const e = d.employee as { first_name_en?: string; last_name_en?: string } | null;
    return {
      employee: e ? `${e.first_name_en ?? ''} ${e.last_name_en ?? ''}`.trim() : '—',
      document_type: String(d.document_type ?? '—'),
      expiry_date: String(d.expiry_date ?? ''),
      days_remaining,
    };
  });
  return {
    columns: [
      { key: 'employee', label: 'Employee' },
      { key: 'document_type', label: 'Document Type' },
      { key: 'expiry_date', label: 'Expiry Date', format: 'date' },
      { key: 'days_remaining', label: 'Days Remaining', format: 'number' },
    ],
    rows,
  };
}

export async function drillIqamaExpiry(companyId?: string | null): Promise<DrilldownResult> {
  const cutoff = new Date(Date.now() + 90 * 86400000).toISOString().slice(0, 10);
  const { data, error } = await co(
    supabase
      .from('employees')
      .select('id, first_name_en, last_name_en, iqama_number, iqama_expiry, nationality, department:departments!department_id(name_en)')
      .eq('is_saudi', false)
      .not('iqama_expiry', 'is', null)
      .lte('iqama_expiry', cutoff)
      // List-only drill; 1000 is the true server cap (db-max-rows).
      .order('iqama_expiry', { ascending: true }).limit(1000),
    companyId,
  );
  if (error) throw new Error(error.message);
  const today_ms = Date.now();
  const rows = (data ?? []).map((e: Record<string, unknown>) => {
    const exp = new Date(String(e.iqama_expiry ?? '')).getTime();
    const days_remaining = isNaN(exp) ? 0 : Math.round((exp - today_ms) / 86400000);
    return {
      employee: `${e.first_name_en ?? ''} ${e.last_name_en ?? ''}`.trim(),
      nationality: String(e.nationality ?? '—'),
      iqama_number: '****' + String(e.iqama_number ?? '').slice(-4), // masked
      department: (e.department as { name_en: string } | null)?.name_en ?? '—',
      iqama_expiry: String(e.iqama_expiry ?? ''),
      days_remaining,
    };
  });
  return {
    columns: [
      { key: 'employee', label: 'Employee' },
      { key: 'nationality', label: 'Nationality' },
      { key: 'iqama_number', label: 'Iqama (masked)' },
      { key: 'department', label: 'Department' },
      { key: 'iqama_expiry', label: 'Expiry Date', format: 'date' },
      { key: 'days_remaining', label: 'Days Left', format: 'number' },
    ],
    rows,
  };
}

export async function drillNitaqatByDept(companyId?: string | null): Promise<DrilldownResult> {
  // Saudization percentages are regulatory (NITAQAT): chunked full fetch over
  // ALL active employees — a capped read skews every department's percentage.
  const data = await fetchAllChunked<Record<string, unknown>>((cursor, chunk) => {
    let q = co(
      supabase
        .from('employees')
        .select('id, is_saudi, status, department:departments!department_id(id, name_en)')
        .eq('status', 'active')
        .order('id', { ascending: true }).limit(chunk),
      companyId,
    );
    if (cursor) q = q.gt('id', cursor);
    return q;
  });

  // Aggregate by department
  const deptMap = new Map<string, { name: string; saudi: number; total: number }>();
  for (const emp of data) {
    const e = emp as Record<string, unknown>;
    const dept = e.department as { id: string; name_en: string } | null;
    const key = dept?.id ?? 'no_dept';
    const entry = deptMap.get(key) ?? { name: dept?.name_en ?? 'No Department', saudi: 0, total: 0 };
    entry.total++;
    if (e.is_saudi) entry.saudi++;
    deptMap.set(key, entry);
  }

  const rows = Array.from(deptMap.values()).map(d => ({
    department: d.name,
    saudi_count: d.saudi,
    total_count: d.total,
    saudization_pct: d.total > 0 ? Math.round((d.saudi / d.total) * 100 * 10) / 10 : 0,
  })).sort((a, b) => a.saudization_pct - b.saudization_pct);

  return {
    columns: [
      { key: 'department', label: 'Department' },
      { key: 'saudi_count', label: 'Saudis', format: 'number' },
      { key: 'total_count', label: 'Total', format: 'number' },
      { key: 'saudization_pct', label: 'Saudization %', format: 'number' },
    ],
    rows,
  };
}

export async function drillTrainingCompletion(_companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await supabase
    .from('training_programs')
    // training_programs has duration_hours + is_active only (no status / target date columns)
    .select('id, title_en, duration_hours, is_active')
    .limit(50);
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((t: Record<string, unknown>) => ({
    program: String(t.title_en ?? '—'),
    duration_hours: Number(t.duration_hours ?? 0),
    is_active: t.is_active ? 'Active' : 'Inactive',
  }));
  return {
    columns: [
      { key: 'program', label: 'Program' },
      { key: 'duration_hours', label: 'Hours', format: 'number' },
      { key: 'is_active', label: 'Status' },
    ],
    rows,
  };
}

export async function drillDisciplinaryCases(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('disciplinary_actions')
      .select('id, action_type, status, created_at, employee:employees!employee_id(first_name_en, last_name_en)')
      .not('status', 'in', '("closed","resolved")')
      .order('created_at', { ascending: false }),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((d: Record<string, unknown>) => {
    const e = d.employee as { first_name_en?: string; last_name_en?: string } | null;
    return {
      employee: e ? `${e.first_name_en ?? ''} ${e.last_name_en ?? ''}`.trim() : '—',
      action_type: String(d.action_type ?? '—'),
      status: String(d.status ?? ''),
      created_at: String(d.created_at ?? ''),
    };
  });
  return {
    columns: [
      { key: 'employee', label: 'Employee' },
      { key: 'action_type', label: 'Action Type' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Date', format: 'date' },
    ],
    rows,
  };
}

// ── Sales / CRM ───────────────────────────────────────────────

export async function drillLeadsByStage(
  companyId?: string | null,
  filters?: Record<string, unknown>,
): Promise<DrilldownResult> {
  let q = supabase
    .from('crm_leads')
    .select('id, title, company_name, stage, estimated_value, expected_close_date, assigned_to:profiles!assigned_to(full_name)')
    .order('created_at', { ascending: false })
    .limit(200);

  if (companyId) q = q.eq('company_id', companyId);
  if (filters?.stage) q = q.eq('stage', String(filters.stage));

  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((l: Record<string, unknown>) => ({
    title: String(l.title ?? '—'),
    company: String(l.company_name ?? '—'),
    stage: String(l.stage ?? '—'),
    assigned_to: (l.assigned_to as { full_name: string } | null)?.full_name ?? '—',
    estimated_value: Number(l.estimated_value ?? 0),
    expected_close_date: String(l.expected_close_date ?? ''),
  }));
  return {
    columns: [
      { key: 'title', label: 'Lead' },
      { key: 'company', label: 'Company' },
      { key: 'stage', label: 'Stage' },
      { key: 'assigned_to', label: 'Assigned To' },
      { key: 'estimated_value', label: 'Value (SAR)', format: 'currency' },
      { key: 'expected_close_date', label: 'Expected Close', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.estimated_value as number), 0),
    totalLabel: 'Total pipeline value',
  };
}

export async function drillQuotationsByStatus(
  companyId?: string | null,
  filters?: Record<string, unknown>,
): Promise<DrilldownResult> {
  let q = supabase
    .from('quotations')
    .select('id, quotation_number, total, status, created_at, customer:customers!customer_id(name)')
    .order('created_at', { ascending: false })
    .limit(200);
  if (companyId) q = q.eq('company_id', companyId);
  if (filters?.status) q = q.eq('status', String(filters.status));
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((q: Record<string, unknown>) => ({
    quotation_number: String(q.quotation_number ?? '—'),
    customer: (q.customer as { name: string } | null)?.name ?? '—',
    status: String(q.status ?? ''),
    total: Number(q.total ?? 0),
    created_at: String(q.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'quotation_number', label: 'Quotation #' },
      { key: 'customer', label: 'Customer' },
      { key: 'status', label: 'Status' },
      { key: 'total', label: 'Value (SAR)', format: 'currency' },
      { key: 'created_at', label: 'Date', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total quotation value',
  };
}

export async function drillSalesRepPerformance(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('quotations')
      // `quotations.created_by` does not exist; `sales_rep_id` is the column with the real
      // foreign key to profiles — and it is also what this drilldown actually means by
      // "sales rep". The old hint made PostgREST reject the whole request.
      .select('id, total, status, sales_rep:profiles!sales_rep_id(full_name)')
      .in('status', ['won', 'sent', 'draft'])
      .gte('created_at', monthStart()),
    companyId,
  );
  if (error) throw new Error(error.message);

  const repMap = new Map<string, { name: string; won: number; pipeline: number; count: number }>();
  for (const q of (data ?? [])) {
    const qr = q as Record<string, unknown>;
    const rep = (qr.sales_rep as { full_name: string } | null)?.full_name ?? 'Unknown';
    const entry = repMap.get(rep) ?? { name: rep, won: 0, pipeline: 0, count: 0 };
    const val = Number(qr.total ?? 0);
    if (qr.status === 'won') { entry.won += val; entry.count++; }
    else { entry.pipeline += val; }
    repMap.set(rep, entry);
  }

  const rows = Array.from(repMap.values())
    .sort((a, b) => b.won - a.won)
    .map(r => ({ rep: r.name, won: r.won, pipeline: r.pipeline, deals: r.count }));

  return {
    columns: [
      { key: 'rep', label: 'Sales Rep' },
      { key: 'deals', label: 'Won Deals', format: 'number' },
      { key: 'won', label: 'Won Value (SAR)', format: 'currency' },
      { key: 'pipeline', label: 'Pipeline (SAR)', format: 'currency' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.won as number), 0),
    totalLabel: 'Total won (MTD)',
  };
}

export async function drillCommissionPayable(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      // commissions (not the commission_records view) carries the FK to profiles; the earning
      // period is period_year / period_month — there is no employee_id or earned_date column.
      .from('commissions')
      .select('id, sales_rep:profiles!sales_rep_id(full_name), commission_amount, status, period_year, period_month')
      .eq('status', 'pending')
      .order('period_year', { ascending: false })
      .order('period_month', { ascending: false })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((c: Record<string, unknown>) => ({
    employee: (c.sales_rep as { full_name?: string } | null)?.full_name ?? '—',
    commission_amount: Number(c.commission_amount ?? 0),
    status: String(c.status ?? ''),
    period: c.period_year && c.period_month
      ? `${c.period_year}-${String(c.period_month).padStart(2, '0')}`
      : '—',
  }));
  return {
    columns: [
      { key: 'employee', label: 'Sales Rep' },
      { key: 'commission_amount', label: 'Commission (SAR)', format: 'currency' },
      { key: 'status', label: 'Status' },
      { key: 'period', label: 'Period' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.commission_amount as number), 0),
    totalLabel: 'Total commission payable',
  };
}

// ── CRM KPI Drilldowns ────────────────────────────────────────
// All loaders read from real Supabase views/tables; company_id is applied as
// an additional guard on top of RLS.

export async function drillCrmNewLeads(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('crm_leads')
      .select('id, title, company_name, contact_name, source, lead_score, assigned_to, created_at, profiles!assigned_to(full_name)')
      .eq('lead_status', 'new').order('created_at', { ascending: false }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    title:       String(r.title ?? '—'),
    company:     String(r.company_name ?? '—'),
    contact:     String(r.contact_name ?? '—'),
    source:      String(r.source ?? '—'),
    score:       Number(r.lead_score ?? 0),
    assigned_to: (r.profiles as { full_name: string } | null)?.full_name ?? 'Unassigned',
    created_at:  String(r.created_at ?? ''),
  }));
  return { columns: [
    { key: 'title', label: 'Lead Title' }, { key: 'company', label: 'Company' },
    { key: 'contact', label: 'Contact' }, { key: 'source', label: 'Source' },
    { key: 'score', label: 'Score', format: 'number' },
    { key: 'assigned_to', label: 'Assigned To' }, { key: 'created_at', label: 'Created', format: 'date' },
  ], rows };
}

export async function drillCrmQualifiedLeads(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('crm_leads')
      .select('id, title, company_name, estimated_value, lead_score, assigned_to, created_at, profiles!assigned_to(full_name)')
      .eq('lead_status', 'qualified').order('estimated_value', { ascending: false }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    title:        String(r.title ?? '—'),
    company:      String(r.company_name ?? '—'),
    value:        Number(r.estimated_value ?? 0),
    score:        Number(r.lead_score ?? 0),
    assigned_to:  (r.profiles as { full_name: string } | null)?.full_name ?? 'Unassigned',
    created_at:   String(r.created_at ?? ''),
  }));
  return { columns: [
    { key: 'title', label: 'Lead' }, { key: 'company', label: 'Company' },
    { key: 'value', label: 'Est. Value (SAR)', format: 'currency' },
    { key: 'score', label: 'Score', format: 'number' },
    { key: 'assigned_to', label: 'Assigned To' }, { key: 'created_at', label: 'Created', format: 'date' },
  ], rows };
}

export async function drillCrmConvertedLeads(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('crm_leads')
      .select('id, title, company_name, estimated_value, converted_at, assigned_to, profiles!assigned_to(full_name)')
      .eq('lead_status', 'converted').order('converted_at', { ascending: false }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    title:        String(r.title ?? '—'),
    company:      String(r.company_name ?? '—'),
    value:        Number(r.estimated_value ?? 0),
    assigned_to:  (r.profiles as { full_name: string } | null)?.full_name ?? 'Unassigned',
    converted_at: String(r.converted_at ?? ''),
  }));
  return { columns: [
    { key: 'title', label: 'Lead' }, { key: 'company', label: 'Company' },
    { key: 'value', label: 'Est. Value (SAR)', format: 'currency' },
    { key: 'assigned_to', label: 'Assigned To' },
    { key: 'converted_at', label: 'Converted', format: 'date' },
  ], rows };
}

export async function drillCrmOpenOpportunities(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('opportunities')
      .select('id, title, stage, estimated_value, probability, weighted_value, expected_close_date, assigned_to, profiles!assigned_to(full_name)')
      .not('stage', 'in', '("won","lost")').order('estimated_value', { ascending: false }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    title:       String(r.title ?? '—'),
    stage:       String(r.stage ?? '—'),
    value:       Number(r.estimated_value ?? 0),
    probability: Number(r.probability ?? 0),
    weighted:    Number(r.weighted_value ?? 0),
    close_date:  String(r.expected_close_date ?? ''),
    assigned_to: (r.profiles as { full_name: string } | null)?.full_name ?? 'Unassigned',
  }));
  return { columns: [
    { key: 'title', label: 'Opportunity' }, { key: 'stage', label: 'Stage', format: 'badge' },
    { key: 'value', label: 'Value (SAR)', format: 'currency' },
    { key: 'probability', label: 'Prob %', format: 'number' },
    { key: 'weighted', label: 'Weighted (SAR)', format: 'currency' },
    { key: 'close_date', label: 'Close Date', format: 'date' },
    { key: 'assigned_to', label: 'Rep' },
  ], rows,
    total: rows.reduce((s, r) => s + (r.value as number), 0),
    totalLabel: 'Total open pipeline',
  };
}

export async function drillCrmPipelineValue(companyId?: string | null): Promise<DrilldownResult> {
  return drillCrmOpenOpportunities(companyId);
}

export async function drillCrmWeightedPipeline(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('sales_pipeline_forecast')
      .select('stage, rep_name, deal_count, total_value, weighted_value, avg_deal_size')
      .order('weighted_value', { ascending: false }).limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    stage:        String(r.stage ?? '—'),
    rep_name:     String(r.rep_name ?? 'Unassigned'),
    deal_count:   Number(r.deal_count ?? 0),
    total_value:  Number(r.total_value ?? 0),
    weighted:     Number(r.weighted_value ?? 0),
  }));
  return { columns: [
    { key: 'stage', label: 'Stage' }, { key: 'rep_name', label: 'Sales Rep' },
    { key: 'deal_count', label: 'Deals', format: 'number' },
    { key: 'total_value', label: 'Total (SAR)', format: 'currency' },
    { key: 'weighted', label: 'Weighted (SAR)', format: 'currency' },
  ], rows,
    total: rows.reduce((s, r) => s + (r.weighted as number), 0),
    totalLabel: 'Total weighted pipeline',
  };
}

export async function drillCrmQuotationSent(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('quotations')
      .select('id, quote_number, title, total, margin_percentage, valid_until, sales_rep_id, customer_id, customers!customer_id(name), profiles!sales_rep_id(full_name)')
      .in('status', ['submitted_to_customer', 'sent']).is('deleted_at', null)
      .order('total', { ascending: false }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    quote_number: String(r.quote_number ?? '—'),
    title:        String(r.title ?? '—'),
    customer:     (r.customers as { name: string } | null)?.name ?? '—',
    rep:          (r.profiles as { full_name: string } | null)?.full_name ?? '—',
    total:        Number(r.total ?? 0),
    margin:       Number(r.margin_percentage ?? 0),
    valid_until:  String(r.valid_until ?? ''),
  }));
  return { columns: [
    { key: 'quote_number', label: 'Quote #' }, { key: 'customer', label: 'Customer' },
    { key: 'rep', label: 'Rep' },
    { key: 'total', label: 'Value (SAR)', format: 'currency' },
    { key: 'margin', label: 'Margin %', format: 'percent' },
    { key: 'valid_until', label: 'Expires', format: 'date' },
  ], rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total quotations sent',
  };
}

export async function drillCrmWonValue(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('quotations')
      .select('id, quote_number, title, total, margin_percentage, deal_won_at, customers!customer_id(name), profiles!sales_rep_id(full_name)')
      .in('status', ['won', 'deal_won']).is('deleted_at', null)
      .order('deal_won_at', { ascending: false }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    quote_number: String(r.quote_number ?? '—'),
    title:        String(r.title ?? '—'),
    customer:     (r.customers as { name: string } | null)?.name ?? '—',
    rep:          (r.profiles as { full_name: string } | null)?.full_name ?? '—',
    total:        Number(r.total ?? 0),
    margin:       Number(r.margin_percentage ?? 0),
    won_at:       String(r.deal_won_at ?? ''),
  }));
  return { columns: [
    { key: 'quote_number', label: 'Quote #' }, { key: 'customer', label: 'Customer' },
    { key: 'rep', label: 'Rep' },
    { key: 'total', label: 'Won Value (SAR)', format: 'currency' },
    { key: 'margin', label: 'Margin %', format: 'percent' },
    { key: 'won_at', label: 'Won Date', format: 'date' },
  ], rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total won value',
  };
}

export async function drillCrmLostValue(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('quotations')
      .select('id, quote_number, title, total, lost_reason, customers!customer_id(name), profiles!sales_rep_id(full_name), updated_at')
      .in('status', ['lost', 'deal_lost']).is('deleted_at', null)
      .order('total', { ascending: false }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    quote_number: String(r.quote_number ?? '—'),
    customer:     (r.customers as { name: string } | null)?.name ?? '—',
    rep:          (r.profiles as { full_name: string } | null)?.full_name ?? '—',
    total:        Number(r.total ?? 0),
    lost_reason:  String(r.lost_reason ?? '—'),
    lost_at:      String(r.updated_at ?? ''),
  }));
  return { columns: [
    { key: 'quote_number', label: 'Quote #' }, { key: 'customer', label: 'Customer' },
    { key: 'rep', label: 'Rep' },
    { key: 'total', label: 'Lost Value (SAR)', format: 'currency' },
    { key: 'lost_reason', label: 'Reason' }, { key: 'lost_at', label: 'Date', format: 'date' },
  ], rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total lost value',
  };
}

export async function drillCrmLostReasons(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('crm_lost_reason_analysis')
      .select('reason_category, lost_count, lost_value').order('lost_count', { ascending: false }),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    reason:      String(r.reason_category ?? '—'),
    lost_count:  Number(r.lost_count ?? 0),
    lost_value:  Number(r.lost_value ?? 0),
  }));
  return { columns: [
    { key: 'reason', label: 'Reason Category' },
    { key: 'lost_count', label: 'Count', format: 'number' },
    { key: 'lost_value', label: 'Lost Value (SAR)', format: 'currency' },
  ], rows };
}

export async function drillCrmOverdueActivities(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('crm_overdue_activities')
      .select('*').order('due_date', { ascending: true }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    subject:     String(r.subject ?? '—'),
    type:        String(r.type ?? '—'),
    due_date:    String(r.due_date ?? ''),
    lead:        String(r.lead_title ?? '—'),
    assigned_to: String(r.assigned_name ?? '—'),
  }));
  return { columns: [
    { key: 'subject', label: 'Activity' }, { key: 'type', label: 'Type', format: 'badge' },
    { key: 'due_date', label: 'Due Date', format: 'date' },
    { key: 'lead', label: 'Lead' }, { key: 'assigned_to', label: 'Assigned To' },
  ], rows };
}

export async function drillCrmStaleOpportunities(companyId?: string | null): Promise<DrilldownResult> {
  const threshold = new Date(Date.now() - 14 * 86400000).toISOString();
  const { data, error } = await co(
    supabase.from('opportunities')
      .select('id, title, stage, estimated_value, stage_changed_at, assigned_to, profiles!assigned_to(full_name)')
      .not('stage', 'in', '("won","lost")')
      .lt('stage_changed_at', threshold)
      .order('stage_changed_at', { ascending: true }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => {
    const staleDays = Math.floor((Date.now() - new Date(r.stage_changed_at as string).getTime()) / 86400000);
    return {
      title:        String(r.title ?? '—'),
      stage:        String(r.stage ?? '—'),
      value:        Number(r.estimated_value ?? 0),
      stale_days:   staleDays,
      assigned_to:  (r.profiles as { full_name: string } | null)?.full_name ?? 'Unassigned',
      last_moved:   String(r.stage_changed_at ?? ''),
    };
  });
  return { columns: [
    { key: 'title', label: 'Opportunity' }, { key: 'stage', label: 'Stage', format: 'badge' },
    { key: 'value', label: 'Value (SAR)', format: 'currency' },
    { key: 'stale_days', label: 'Days Stale', format: 'number' },
    { key: 'assigned_to', label: 'Rep' }, { key: 'last_moved', label: 'Last Move', format: 'date' },
  ], rows };
}

export async function drillCrmExpiringQuotations(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('quotation_expiry_alerts')
      // the view exposes quotation_id (no id) and sales_rep_name (no rep_name)
      .select('quotation_id, quote_number, customer_name, sales_rep_name, total, valid_until, expiry_status')
      .not('expiry_status', 'eq', 'active').order('valid_until', { ascending: true }).limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    quote_number:   String(r.quote_number ?? '—'),
    customer:       String(r.customer_name ?? '—'),
    rep:            String(r.sales_rep_name ?? '—'),
    total:          Number(r.total ?? 0),
    valid_until:    String(r.valid_until ?? ''),
    expiry_status:  String(r.expiry_status ?? '—'),
  }));
  return { columns: [
    { key: 'quote_number', label: 'Quote #' }, { key: 'customer', label: 'Customer' },
    { key: 'rep', label: 'Rep' },
    { key: 'total', label: 'Value (SAR)', format: 'currency' },
    { key: 'valid_until', label: 'Expires', format: 'date' },
    { key: 'expiry_status', label: 'Status', format: 'badge' },
  ], rows };
}

export async function drillCrmTargetAchievement(companyId?: string | null): Promise<DrilldownResult> {
  const now = new Date();
  const { data, error } = await co(
    supabase.from('sales_targets')
      .select('id, target_amount, achieved_amount, period_month, period_year, profiles!sales_rep_id(full_name)')
      .eq('period_month', now.getMonth() + 1).eq('period_year', now.getFullYear())
      .order('target_amount', { ascending: false }).limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => {
    const target   = Number(r.target_amount ?? 0);
    const achieved = Number(r.achieved_amount ?? 0);
    return {
      rep:          (r.profiles as { full_name: string } | null)?.full_name ?? '—',
      target:       target,
      achieved:     achieved,
      achievement:  target > 0 ? Math.round((achieved / target) * 100) : 0,
    };
  });
  return { columns: [
    { key: 'rep', label: 'Sales Rep' },
    { key: 'target', label: 'Target (SAR)', format: 'currency' },
    { key: 'achieved', label: 'Achieved (SAR)', format: 'currency' },
    { key: 'achievement', label: 'Achievement %', format: 'percent' },
  ], rows };
}

export async function drillCrmRevenueByChannel(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('quotations')
      .select('id, quote_number, sales_channel, total, status, customers!customer_id(name)')
      .in('status', ['won', 'deal_won']).is('deleted_at', null)
      .order('total', { ascending: false }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    channel:  String(r.sales_channel ?? '—'),
    customer: (r.customers as { name: string } | null)?.name ?? '—',
    total:    Number(r.total ?? 0),
  }));
  return { columns: [
    { key: 'channel', label: 'Channel', format: 'badge' },
    { key: 'customer', label: 'Customer' },
    { key: 'total', label: 'Won Value (SAR)', format: 'currency' },
  ], rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total won by channel',
  };
}

export async function drillCrmRevenueBySector(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('quotations')
      .select('id, quote_number, total, customers!customer_id(name, sector)')
      .in('status', ['won', 'deal_won']).is('deleted_at', null)
      .order('total', { ascending: false }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => {
    const c = r.customers as { name: string; sector: string | null } | null;
    return { customer: c?.name ?? '—', sector: c?.sector ?? 'Unknown', total: Number(r.total ?? 0) };
  });
  return { columns: [
    { key: 'sector', label: 'Sector' }, { key: 'customer', label: 'Customer' },
    { key: 'total', label: 'Won Value (SAR)', format: 'currency' },
  ], rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total won by sector',
  };
}

export async function drillCrmMarginExceptions(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('quotations')
      .select('id, quote_number, title, total, margin_percentage, approval_stage, customers!customer_id(name), profiles!sales_rep_id(full_name)')
      .not('status', 'in', '("won","deal_won","lost","deal_lost","converted")')
      .not('margin_percentage', 'is', null)
      .lt('margin_percentage', 35).is('deleted_at', null)
      .order('margin_percentage', { ascending: true }).limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    quote_number: String(r.quote_number ?? '—'),
    title:        String(r.title ?? '—'),
    customer:     (r.customers as { name: string } | null)?.name ?? '—',
    rep:          (r.profiles as { full_name: string } | null)?.full_name ?? '—',
    total:        Number(r.total ?? 0),
    margin:       Number(r.margin_percentage ?? 0),
    stage:        String(r.approval_stage ?? '—'),
  }));
  return { columns: [
    { key: 'quote_number', label: 'Quote #' }, { key: 'customer', label: 'Customer' },
    { key: 'rep', label: 'Rep' },
    { key: 'total', label: 'Value (SAR)', format: 'currency' },
    { key: 'margin', label: 'Margin %', format: 'percent', sensitive: true },
    { key: 'stage', label: 'Approval Stage', format: 'badge' },
  ], rows };
}

export async function drillCrmPendingApprovals(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('quotations')
      .select('id, quote_number, title, total, margin_percentage, approval_stage, customers!customer_id(name), profiles!sales_rep_id(full_name), submitted_at')
      .not('approval_stage', 'in', '("none","approved","rejected","changes_requested")')
      .is('deleted_at', null)
      .order('submitted_at', { ascending: true }).limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    quote_number:   String(r.quote_number ?? '—'),
    customer:       (r.customers as { name: string } | null)?.name ?? '—',
    rep:            (r.profiles as { full_name: string } | null)?.full_name ?? '—',
    total:          Number(r.total ?? 0),
    approval_stage: String(r.approval_stage ?? '—'),
    submitted_at:   String(r.submitted_at ?? ''),
  }));
  return { columns: [
    { key: 'quote_number', label: 'Quote #' }, { key: 'customer', label: 'Customer' },
    { key: 'rep', label: 'Rep' },
    { key: 'total', label: 'Value (SAR)', format: 'currency' },
    { key: 'approval_stage', label: 'Stage', format: 'badge' },
    { key: 'submitted_at', label: 'Submitted', format: 'date' },
  ], rows };
}

export async function drillCrmCreditHoldCustomers(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('customers')
      .select('id, name, email, phone, credit_limit, outstanding_balance, credit_hold, type')
      .eq('credit_hold', true).order('outstanding_balance', { ascending: false }).limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    name:               String(r.name ?? '—'),
    type:               String(r.type ?? '—'),
    credit_limit:       Number(r.credit_limit ?? 0),
    outstanding:        Number(r.outstanding_balance ?? 0),
    email:              String(r.email ?? '—'),
  }));
  return { columns: [
    { key: 'name', label: 'Customer' }, { key: 'type', label: 'Type' },
    { key: 'credit_limit', label: 'Credit Limit (SAR)', format: 'currency', sensitive: true },
    { key: 'outstanding', label: 'Outstanding (SAR)', format: 'currency', sensitive: true },
    { key: 'email', label: 'Email' },
  ], rows };
}

export async function drillCrmCollectionRisk(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('won_deal_approvals')
      .select('id, po_number, status, finance_approved_at, quotations!quotation_id(quote_number, title, total, customers!customer_id(name))')
      .eq('status', 'approved').order('finance_approved_at', { ascending: true }).limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => {
    const q = r.quotations as Record<string, unknown> | null;
    const c = q?.customers as { name: string } | null;
    return {
      po_number:    String(r.po_number ?? '—'),
      quote_number: String(q?.quote_number ?? '—'),
      customer:     c?.name ?? '—',
      total:        Number(q?.total ?? 0),
      approved_at:  String(r.finance_approved_at ?? ''),
    };
  });
  return { columns: [
    { key: 'po_number', label: 'PO #' }, { key: 'quote_number', label: 'Quote #' },
    { key: 'customer', label: 'Customer' },
    { key: 'total', label: 'Deal Value (SAR)', format: 'currency', sensitive: true },
    { key: 'approved_at', label: 'Finance Approved', format: 'date' },
  ], rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total collection exposure',
  };
}

// ── Sprint 1: Revenue by product category ─────────────────────
export async function drillCrmRevenueByProductCategory(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('v_revenue_by_product_category')
      .select('product_category,won_deals,revenue,avg_margin_pct')
      .order('revenue', { ascending: false })
      .limit(50),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    category:    String(r.product_category ?? 'Uncategorised'),
    won_deals:   Number(r.won_deals ?? 0),
    revenue:     Number(r.revenue ?? 0),
    avg_margin:  Number(r.avg_margin_pct ?? 0),
  }));
  return {
    columns: [
      { key: 'category', label: 'Product Category' },
      { key: 'won_deals', label: 'Won Deals', format: 'number' },
      { key: 'revenue', label: 'Revenue (SAR)', format: 'currency' },
      { key: 'avg_margin', label: 'Avg Margin %', format: 'percent' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.revenue as number), 0),
    totalLabel: 'Total revenue by category',
  };
}

// ── Sprint 2: Dormant customers ───────────────────────────────
export async function drillCrmDormantCustomers(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase.from('v_dormant_customers')
      .select('customer_name,sector,days_since_activity,open_opps,lifetime_revenue,last_activity_at')
      .order('lifetime_revenue', { ascending: false })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    customer:         String(r.customer_name ?? '—'),
    sector:           String(r.sector ?? '—'),
    days_inactive:    Number(r.days_since_activity ?? 0),
    lifetime_revenue: Number(r.lifetime_revenue ?? 0),
    last_activity:    String(r.last_activity_at ?? ''),
  }));
  return {
    columns: [
      { key: 'customer', label: 'Customer' },
      { key: 'sector', label: 'Sector' },
      { key: 'days_inactive', label: 'Days Inactive', format: 'number' },
      { key: 'lifetime_revenue', label: 'Lifetime Revenue (SAR)', format: 'currency' },
      { key: 'last_activity', label: 'Last Activity', format: 'date' },
    ],
    rows,
  };
}

// ── Procurement ───────────────────────────────────────────────

export async function drillOpenPurchaseOrders(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('purchase_orders')
      .select('id, po_number, status, created_at, supplier:suppliers!supplier_id(name)')
      .in('status', ['draft', 'approved', 'ordered', 'partial'])
      .order('created_at', { ascending: false })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((p: Record<string, unknown>) => ({
    po_number: String(p.po_number ?? '—'),
    supplier: (p.supplier as { name: string } | null)?.name ?? '—',
    status: String(p.status ?? ''),
    created_at: String(p.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'po_number', label: 'PO #' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Date', format: 'date' },
    ],
    rows,
  };
}

export async function drillPendingSupplierInvoices(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('supplier_invoices')
      .select('id, purchase_invoice_number, total, due_date, status, supplier:suppliers!supplier_id(name)')
      .in('status', ['received', 'approved', 'matched'])
      .order('due_date', { ascending: true })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((i: Record<string, unknown>) => ({
    invoice_number: String(i.purchase_invoice_number ?? '—'),
    supplier: (i.supplier as { name: string } | null)?.name ?? '—',
    due_date: String(i.due_date ?? ''),
    status: String(i.status ?? ''),
    total: Number(i.total ?? 0),
  }));
  return {
    columns: [
      { key: 'invoice_number', label: 'Invoice #' },
      { key: 'supplier', label: 'Supplier' },
      { key: 'due_date', label: 'Due Date', format: 'date' },
      { key: 'status', label: 'Status' },
      { key: 'total', label: 'Amount (SAR)', format: 'currency' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total as number), 0),
    totalLabel: 'Total pending AP',
  };
}

export async function drillThreeWayMatchExceptions(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('invoice_match_exceptions')
      .select('id, exception_type, invoice_id, status, created_at')
      .eq('status', 'open')
      .order('created_at', { ascending: false })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((e: Record<string, unknown>) => ({
    exception_type: String(e.exception_type ?? '—'),
    status: String(e.status ?? ''),
    created_at: String(e.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'exception_type', label: 'Exception Type' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Detected', format: 'date' },
    ],
    rows,
  };
}

// ── Warehouse ─────────────────────────────────────────────────

export async function drillInventoryValue(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('inventory_items')
      .select('id, product:products!product_id(code, name), quantity_on_hand, unit_cost, reorder_point')
      .gt('quantity_on_hand', 0)
      .order('quantity_on_hand', { ascending: false })
      .limit(500),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((i: Record<string, unknown>) => ({
    item_code: productOf(i).code,
    item_name: productOf(i).name,
    qty: Number(i.quantity_on_hand ?? 0),
    unit_cost: Number(i.unit_cost ?? 0),
    total_value: Number(i.quantity_on_hand ?? 0) * Number(i.unit_cost ?? 0),
    reorder_point: Number(i.reorder_point ?? 0),
  }));
  return {
    columns: [
      { key: 'item_code', label: 'Code' },
      { key: 'item_name', label: 'Item' },
      { key: 'qty', label: 'On Hand', format: 'number' },
      { key: 'unit_cost', label: 'Unit Cost (SAR)', format: 'currency' },
      { key: 'total_value', label: 'Total Value (SAR)', format: 'currency' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.total_value as number), 0),
    totalLabel: 'Total inventory value',
  };
}

export async function drillStockouts(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('inventory_items')
      // inventory_items has no last-received column; item code/name come from products
      .select('id, product:products!product_id(code, name), quantity_on_hand, reorder_point')
      .eq('quantity_on_hand', 0)
      .order('id'),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? [])
    .map((i: Record<string, unknown>) => ({
      item_code: productOf(i).code,
      item_name: productOf(i).name,
      qty: 0,
      reorder_point: Number(i.reorder_point ?? 0),
    }))
    .sort((a, b) => a.item_name.localeCompare(b.item_name));
  return {
    columns: [
      { key: 'item_code', label: 'Code' },
      { key: 'item_name', label: 'Item' },
      { key: 'qty', label: 'On Hand', format: 'number' },
      { key: 'reorder_point', label: 'Reorder Point', format: 'number' },
    ],
    rows,
  };
}

export async function drillReorderAlerts(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('inventory_items')
      .select('id, product:products!product_id(code, name), quantity_on_hand, reorder_point')
      // PostgREST can't compare two columns in .filter (3rd arg is a literal);
      // fetch reorder-tracked items ordered by lowest stock and compare in code.
      .gt('reorder_point', 0)
      .order('quantity_on_hand', { ascending: true })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? [])
    .filter((i: Record<string, unknown>) => Number(i.quantity_on_hand ?? 0) <= Number(i.reorder_point ?? 0))
    .map((i: Record<string, unknown>) => ({
      item_code: productOf(i).code,
      item_name: productOf(i).name,
      qty: Number(i.quantity_on_hand ?? 0),
      reorder_point: Number(i.reorder_point ?? 0),
      shortage: Number(i.reorder_point ?? 0) - Number(i.quantity_on_hand ?? 0),
    }));
  return {
    columns: [
      { key: 'item_code', label: 'Code' },
      { key: 'item_name', label: 'Item' },
      { key: 'qty', label: 'On Hand', format: 'number' },
      { key: 'reorder_point', label: 'Reorder Point', format: 'number' },
      { key: 'shortage', label: 'Shortage', format: 'number' },
    ],
    rows,
  };
}

export async function drillSlowMovingStock(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('inventory_items')
      // inventory_items has no movement timestamp; updated_at is trigger-maintained and bumps on
      // every on-hand / reserved / cost change, so it is the last stock change for the row.
      .select('id, product:products!product_id(code, name), quantity_on_hand, unit_cost, updated_at')
      .gt('quantity_on_hand', 0)
      .lte('updated_at', ninetyDaysAgo())
      .order('updated_at', { ascending: true })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const today_ms = Date.now();
  const rows = (data ?? []).map((i: Record<string, unknown>) => {
    const moved = new Date(String(i.updated_at ?? '')).getTime();
    return {
      item_code: productOf(i).code,
      item_name: productOf(i).name,
      qty: Number(i.quantity_on_hand ?? 0),
      value: Number(i.quantity_on_hand ?? 0) * Number(i.unit_cost ?? 0),
      updated_at: String(i.updated_at ?? ''),
      days_idle: isNaN(moved) ? 0 : Math.floor((today_ms - moved) / 86400000),
    };
  });
  return {
    columns: [
      { key: 'item_code', label: 'Code' },
      { key: 'item_name', label: 'Item' },
      { key: 'qty', label: 'On Hand', format: 'number' },
      { key: 'value', label: 'Value (SAR)', format: 'currency' },
      { key: 'days_idle', label: 'Days Idle', format: 'number' },
      { key: 'updated_at', label: 'Last Stock Change', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.value as number), 0),
    totalLabel: 'Total slow-moving value',
  };
}

export async function drillCycleCountVariances(companyId?: string | null): Promise<DrilldownResult> {
  // cycle_count_items has no company_id, item code/name or count timestamp of its own: company
  // scope and the count date come from the parent cycle_counts row, the item from products.
  let q = supabase
    .from('cycle_count_items')
    .select('id, product:products!product_id(code, name), system_quantity, counted_quantity, variance, variance_value, cycle_count:cycle_counts!inner(company_id, completed_at)')
    .neq('variance', 0)
    .order('variance_value', { ascending: false })
    .limit(200);
  if (companyId) q = q.eq('cycle_counts.company_id', companyId);
  const { data, error } = await q;
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((c: Record<string, unknown>) => ({
    item_code: productOf(c).code,
    item_name: productOf(c).name,
    system_quantity: Number(c.system_quantity ?? 0),
    counted_quantity: Number(c.counted_quantity ?? 0),
    variance: Number(c.variance ?? 0),
    variance_value: Number(c.variance_value ?? 0),
    completed_at: String((c.cycle_count as { completed_at?: string | null } | null)?.completed_at ?? ''),
  }));
  return {
    columns: [
      { key: 'item_code', label: 'Code' },
      { key: 'item_name', label: 'Item' },
      { key: 'system_quantity', label: 'System Qty', format: 'number' },
      { key: 'counted_quantity', label: 'Counted Qty', format: 'number' },
      { key: 'variance', label: 'Variance', format: 'number' },
      { key: 'variance_value', label: 'Variance Value (SAR)', format: 'currency' },
      { key: 'completed_at', label: 'Count Date', format: 'date' },
    ],
    rows,
    total: rows.reduce((s, r) => s + Math.abs(r.variance_value as number), 0),
    totalLabel: 'Total variance value',
  };
}

// ── Manufacturing ─────────────────────────────────────────────

export async function drillProductionOrders(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('production_orders')
      // production_orders has quantity / planned_start / planned_end (no produced-quantity column)
      .select('id, product:products!product_id(name, sku), quantity, status, planned_start, planned_end')
      .in('status', ['draft', 'released', 'in_progress'])
      .order('planned_end', { ascending: true })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((p: Record<string, unknown>) => ({
    product: (p.product as { name: string } | null)?.name ?? '—',
    sku: (p.product as { sku?: string } | null)?.sku ?? '—',
    quantity: Number(p.quantity ?? 0),
    status: String(p.status ?? ''),
    start_date: String(p.planned_start ?? ''),
    end_date: String(p.planned_end ?? ''),
  }));
  return {
    columns: [
      { key: 'product', label: 'Product' },
      { key: 'sku', label: 'SKU' },
      { key: 'quantity', label: 'Planned', format: 'number' },
      { key: 'status', label: 'Status' },
      { key: 'start_date', label: 'Start', format: 'date' },
      { key: 'end_date', label: 'End', format: 'date' },
    ],
    rows,
  };
}

export async function drillWorkOrders(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('work_orders')
      .select('id, wo_number, product:products!product_id(name), quantity_planned, status, planned_end_date')
      .in('status', ['draft', 'released', 'in_progress'])
      .order('planned_end_date', { ascending: true })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((w: Record<string, unknown>) => ({
    wo_number: String(w.wo_number ?? '—'),
    product: (w.product as { name: string } | null)?.name ?? '—',
    quantity_planned: Number(w.quantity_planned ?? 0),
    status: String(w.status ?? ''),
    planned_end_date: String(w.planned_end_date ?? ''),
  }));
  return {
    columns: [
      { key: 'wo_number', label: 'WO #' },
      { key: 'product', label: 'Product' },
      { key: 'quantity_planned', label: 'Qty Planned', format: 'number' },
      { key: 'status', label: 'Status' },
      { key: 'planned_end_date', label: 'Due Date', format: 'date' },
    ],
    rows,
  };
}

export async function drillMrpExceptions(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('mrp_planned_orders')
      // mrp_planned_orders stores the net requirement as planned_qty (no gross / on-hand
      // columns); item code + name come from products.
      .select('id, product:products!product_id(code, name), planned_qty, order_type, planned_date')
      .gt('planned_qty', 0)
      .order('planned_date', { ascending: true })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((m: Record<string, unknown>) => ({
    item_name: productOf(m).name,
    item_sku: productOf(m).code,
    planned_qty: Number(m.planned_qty ?? 0),
    order_type: String(m.order_type ?? '—'),
    planned_date: String(m.planned_date ?? ''),
  }));
  return {
    columns: [
      { key: 'item_name', label: 'Item' },
      { key: 'item_sku', label: 'SKU' },
      { key: 'planned_qty', label: 'Net Req', format: 'number' },
      { key: 'order_type', label: 'Action' },
      { key: 'planned_date', label: 'Planned Date', format: 'date' },
    ],
    rows,
  };
}

// drillOeeMetrics was removed: machine_telemetry_events holds alarm / speed / temperature / power
// readings and no availability, performance, quality or OEE columns, so an OEE drill-down cannot
// be built from it (see docs/wiring/deprecation-register-2026-09-06.md).

// ── Projects ──────────────────────────────────────────────────

export async function drillProjectBudget(companyId?: string | null): Promise<DrilldownResult> {
  // Reduced total is a budget figure: chunked full fetch (see drillRevenueMtd).
  const data = await fetchAllChunked<Record<string, unknown>>((cursor, chunk) => {
    let q = co(
      supabase
        .from('projects')
        .select('id, name, status, budget, actual_cost, start_date, end_date, manager:profiles!manager_id(full_name)')
        .in('status', ['active', 'on_hold'])
        .order('id', { ascending: true }).limit(chunk),
      companyId,
    );
    if (cursor) q = q.gt('id', cursor);
    return q;
  });
  data.sort((a, b) => String(a.name ?? '').localeCompare(String(b.name ?? '')));
  const rows = data.map((p: Record<string, unknown>) => {
    const budget = Number(p.budget ?? 0);
    const actual = Number(p.actual_cost ?? 0);
    return {
      project: String(p.name ?? '—'),
      manager: (p.manager as { full_name: string } | null)?.full_name ?? '—',
      status: String(p.status ?? ''),
      budget,
      actual,
      variance: budget - actual,
      utilization_pct: budget > 0 ? Math.round((actual / budget) * 100) : 0,
    };
  });
  return {
    columns: [
      { key: 'project', label: 'Project' },
      { key: 'manager', label: 'Manager' },
      { key: 'status', label: 'Status' },
      { key: 'budget', label: 'Budget (SAR)', format: 'currency' },
      { key: 'actual', label: 'Actual (SAR)', format: 'currency' },
      { key: 'variance', label: 'Variance (SAR)', format: 'currency' },
      { key: 'utilization_pct', label: 'Utilization %', format: 'number' },
    ],
    rows,
    total: rows.reduce((s, r) => s + (r.budget as number), 0),
    totalLabel: 'Total budget',
  };
}

export async function drillMilestoneDelays(companyId?: string | null): Promise<DrilldownResult> {
  const today = new Date().toISOString().slice(0, 10);
  const { data, error } = await supabase
    .from('project_milestones')
    // project_milestones has title + completed_at (no name / status columns): open = not completed
    .select('id, title, due_date, completed_at, project:projects!project_id(name, company_id)')
    .is('completed_at', null)
    .lt('due_date', today)
    .order('due_date', { ascending: true })
    .limit(100);
  if (error) throw new Error(error.message);
  const today_ms = Date.now();
  const rows = (data ?? [])
    .filter((m: Record<string, unknown>) => {
      const proj = m.project as { company_id?: string } | null;
      return !companyId || proj?.company_id === companyId;
    })
    .map((m: Record<string, unknown>) => {
      const due = new Date(String(m.due_date ?? '')).getTime();
      return {
        project: (m.project as { name: string } | null)?.name ?? '—',
        milestone: String(m.title ?? '—'),
        due_date: String(m.due_date ?? ''),
        days_overdue: isNaN(due) ? 0 : Math.floor((today_ms - due) / 86400000),
      };
    });
  return {
    columns: [
      { key: 'project', label: 'Project' },
      { key: 'milestone', label: 'Milestone' },
      { key: 'due_date', label: 'Due Date', format: 'date' },
      { key: 'days_overdue', label: 'Days Overdue', format: 'number' },
    ],
    rows,
  };
}

export async function drillProjectRisks(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await supabase
    .from('project_risks')
    .select('id, title, likelihood, impact, risk_score, status, mitigation_plan, project:projects!project_id(name, company_id)')
    .not('status', 'in', '("closed","accepted")')
    .order('risk_score', { ascending: false })
    .limit(100);
  if (error) throw new Error(error.message);
  const rows = (data ?? [])
    .filter((r: Record<string, unknown>) => {
      const proj = r.project as { company_id?: string } | null;
      return !companyId || proj?.company_id === companyId;
    })
    .map((r: Record<string, unknown>) => ({
      project: (r.project as { name: string } | null)?.name ?? '—',
      title: String(r.title ?? '—'),
      likelihood: String(r.likelihood ?? '—'),
      impact: String(r.impact ?? '—'),
      risk_score: Number(r.risk_score ?? 0),
      status: String(r.status ?? ''),
      mitigation_plan: r.mitigation_plan ? 'Yes' : 'None',
    }));
  return {
    columns: [
      { key: 'project', label: 'Project' },
      { key: 'title', label: 'Risk' },
      { key: 'likelihood', label: 'Likelihood' },
      { key: 'impact', label: 'Impact' },
      { key: 'risk_score', label: 'Risk Score', format: 'number' },
      { key: 'status', label: 'Status' },
    ],
    rows,
  };
}

// ── Compliance / Risk ─────────────────────────────────────────

export async function drillOpenRisks(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('risk_register')
      .select('id, title, category, likelihood, impact, risk_score, status, risk_owner, created_at')
      .not('status', 'in', '("closed","mitigated")')
      .order('risk_score', { ascending: false })
      .limit(100),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    title: String(r.title ?? '—'),
    category: String(r.category ?? '—'),
    likelihood: String(r.likelihood ?? '—'),
    impact: String(r.impact ?? '—'),
    risk_score: Number(r.risk_score ?? 0),
    status: String(r.status ?? ''),
    created_at: String(r.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'title', label: 'Risk' },
      { key: 'category', label: 'Category' },
      { key: 'likelihood', label: 'Likelihood' },
      { key: 'impact', label: 'Impact' },
      { key: 'risk_score', label: 'Score', format: 'number' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Raised', format: 'date' },
    ],
    rows,
  };
}

export async function drillAuditFindings(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('audit_findings')
      // audit_findings has no title column; description is the finding text
      .select('id, description, severity, status, due_date, created_at')
      .not('status', 'in', '("closed","resolved")')
      .order('due_date', { ascending: true })
      .limit(100),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((f: Record<string, unknown>) => ({
    description: String(f.description ?? '—'),
    severity: String(f.severity ?? '—'),
    status: String(f.status ?? ''),
    due_date: String(f.due_date ?? ''),
    created_at: String(f.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'description', label: 'Finding' },
      { key: 'severity', label: 'Severity' },
      { key: 'status', label: 'Status' },
      { key: 'due_date', label: 'Due Date', format: 'date' },
      { key: 'created_at', label: 'Raised', format: 'date' },
    ],
    rows,
  };
}

export async function drillPdplRequests(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('pdpl_requests')
      .select('id, request_type, status, created_at, deadline')
      .not('status', 'in', '("completed","rejected")')
      .order('deadline', { ascending: true })
      .limit(100),
    companyId,
  );
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((r: Record<string, unknown>) => ({
    request_type: String(r.request_type ?? '—'),
    status: String(r.status ?? ''),
    created_at: String(r.created_at ?? ''),
    deadline: String(r.deadline ?? ''),
  }));
  return {
    columns: [
      { key: 'request_type', label: 'Request Type' },
      { key: 'status', label: 'Status' },
      { key: 'created_at', label: 'Received', format: 'date' },
      { key: 'deadline', label: 'Due Date', format: 'date' },
    ],
    rows,
  };
}

export async function drillMfaViolations(_companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await supabase
    .from('auth_security_events')
    .select('id, event_type, user_id, ip_address, created_at, metadata')
    .in('event_type', ['mfa_failed', 'mfa_bypassed', 'suspicious_login'])
    .gte('created_at', thirtyDaysAgo())
    .order('created_at', { ascending: false })
    .limit(100);
  if (error) throw new Error(error.message);
  const rows = (data ?? []).map((e: Record<string, unknown>) => ({
    event_type: String(e.event_type ?? '—'),
    user_id: String(e.user_id ?? '—').slice(0, 8) + '…',
    ip_address: String(e.ip_address ?? '—'),
    created_at: String(e.created_at ?? ''),
  }));
  return {
    columns: [
      { key: 'event_type', label: 'Event Type' },
      { key: 'user_id', label: 'User (masked)' },
      { key: 'ip_address', label: 'IP Address' },
      { key: 'created_at', label: 'Timestamp', format: 'date' },
    ],
    rows,
  };
}

// ── Approvals ─────────────────────────────────────────────────

export async function drillPendingApprovals(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('approval_requests')
      // approval_requests has entity_type + submitted_by (no request_type / requested_by columns)
      .select('id, entity_type, submitted_by, current_step, created_at')
      .eq('status', 'pending')
      .order('created_at', { ascending: true })
      .limit(200),
    companyId,
  );
  if (error) throw new Error(error.message);
  const names = await submitterNames(data ?? []);
  const today_ms = Date.now();
  const rows = (data ?? []).map((a: Record<string, unknown>) => {
    const created = new Date(String(a.created_at ?? '')).getTime();
    return {
      request_type: String(a.entity_type ?? '—'),
      requested_by: submitterOf(names, a),
      current_step: String(a.current_step ?? '1'),
      days_waiting: isNaN(created) ? 0 : Math.floor((today_ms - created) / 86400000),
      created_at: String(a.created_at ?? ''),
    };
  });
  return {
    columns: [
      { key: 'request_type', label: 'Type' },
      { key: 'requested_by', label: 'Requested By' },
      { key: 'current_step', label: 'Step' },
      { key: 'days_waiting', label: 'Days Waiting', format: 'number' },
      { key: 'created_at', label: 'Submitted', format: 'date' },
    ],
    rows,
  };
}

export async function drillApprovalAging(companyId?: string | null): Promise<DrilldownResult> {
  const sevenDaysAgo = new Date(Date.now() - 7 * 86400000).toISOString();
  const { data, error } = await co(
    supabase
      .from('approval_requests')
      .select('id, entity_type, submitted_by, created_at')
      .eq('status', 'pending')
      .lt('created_at', sevenDaysAgo)
      .order('created_at', { ascending: true })
      .limit(100),
    companyId,
  );
  if (error) throw new Error(error.message);
  const names = await submitterNames(data ?? []);
  const today_ms = Date.now();
  const rows = (data ?? []).map((a: Record<string, unknown>) => {
    const created = new Date(String(a.created_at ?? '')).getTime();
    const days = isNaN(created) ? 0 : Math.floor((today_ms - created) / 86400000);
    return {
      request_type: String(a.entity_type ?? '—'),
      requested_by: submitterOf(names, a),
      days_overdue: days,
      sla_bucket: days > 30 ? '>30 days' : days > 14 ? '14-30 days' : '7-14 days',
      created_at: String(a.created_at ?? ''),
    };
  });
  return {
    columns: [
      { key: 'request_type', label: 'Type' },
      { key: 'requested_by', label: 'Requested By' },
      { key: 'sla_bucket', label: 'SLA Bucket' },
      { key: 'days_overdue', label: 'Days Waiting', format: 'number' },
      { key: 'created_at', label: 'Submitted', format: 'date' },
    ],
    rows,
  };
}

export async function drillRejectedApprovals(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('approval_requests')
      // the engine records the approver's rejection text in reason_note (reason_code = 'rejected_by_approver')
      .select('id, entity_type, submitted_by, reason_note, updated_at')
      .eq('status', 'rejected')
      .gte('updated_at', thirtyDaysAgo())
      .order('updated_at', { ascending: false })
      .limit(100),
    companyId,
  );
  if (error) throw new Error(error.message);
  const names = await submitterNames(data ?? []);
  const rows = (data ?? []).map((a: Record<string, unknown>) => ({
    request_type: String(a.entity_type ?? '—'),
    requested_by: submitterOf(names, a),
    reason_note: String(a.reason_note ?? '—'),
    updated_at: String(a.updated_at ?? ''),
  }));
  return {
    columns: [
      { key: 'request_type', label: 'Type' },
      { key: 'requested_by', label: 'Requested By' },
      { key: 'reason_note', label: 'Rejection Reason' },
      { key: 'updated_at', label: 'Rejected On', format: 'date' },
    ],
    rows,
  };
}

export async function drillEscalatedApprovals(companyId?: string | null): Promise<DrilldownResult> {
  const { data, error } = await co(
    supabase
      .from('approval_requests')
      // escalation is recorded as escalation_level (+ sla_deadline); there is no escalated_at or
      // escalation_reason column, so updated_at (the status transition) orders the list.
      .select('id, entity_type, submitted_by, escalation_level, sla_deadline, updated_at')
      .eq('status', 'escalated')
      .order('updated_at', { ascending: false })
      .limit(100),
    companyId,
  );
  if (error) throw new Error(error.message);
  const names = await submitterNames(data ?? []);
  const rows = (data ?? []).map((a: Record<string, unknown>) => ({
    request_type: String(a.entity_type ?? '—'),
    requested_by: submitterOf(names, a),
    escalation_level: Number(a.escalation_level ?? 0),
    sla_deadline: String(a.sla_deadline ?? ''),
    updated_at: String(a.updated_at ?? ''),
  }));
  return {
    columns: [
      { key: 'request_type', label: 'Type' },
      { key: 'requested_by', label: 'Requested By' },
      { key: 'escalation_level', label: 'Escalation Level', format: 'number' },
      { key: 'sla_deadline', label: 'SLA Deadline', format: 'date' },
      { key: 'updated_at', label: 'Last Updated', format: 'date' },
    ],
    rows,
  };
}
